import produce from 'immer';
import { useCallback } from 'react';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useGetPublicCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { selectedBoardsSelector, selectedClipItemsSelector } from '~/store/selectedItems/selectors';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { canChangeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export const usePublicSelectionMenuOptions = () => {
  const { permissions } = usePublicPermissions();
  const { getCustomFieldsMenuOptions } = useGetPublicCustomFieldsMenuOptions();

  const store = useAirStore();

  const canEditCustomFields = canChangeBoardCustomFields(permissions);

  const getSelectionMenuOptions: GetSelectionMenuOptionsType = useCallback(() => {
    const selectedClips = selectedClipItemsSelector(store.getState());

    const selectedBoards = selectedBoardsSelector(store.getState());

    const options = produce<DropdownMenuOption[]>([], (draft) => {
      if (canEditCustomFields) {
        const customFieldsMenuOptions = getCustomFieldsMenuOptions({
          boards: selectedBoards,
          assets: selectedClips.map((s) => s.item),
        });
        if (customFieldsMenuOptions.length) {
          draft.splice(3, 0, {
            ...getDropdownMenuOption('bulk-edit-custom-fields'),
            options: customFieldsMenuOptions,
            type: 'sub',
          });
        }
      }

      return draft;
    });

    return options;
  }, [store, canEditCustomFields, getCustomFieldsMenuOptions]);

  return {
    getSelectionMenuOptions,
  };
};
